const { getCredential } = require("./auth/getCredentials");
const { QueueServiceClient, QueueClient, StorageSharedKeyCredential } = require("@azure/storage-queue");
const { AderantAzureStorageError } = require("./utils/errors");
const cache = require('node-cache');

let clientCache = new cache({stdTTL: 3600, useClones: false});

/**
 * Get the client for the current context. This client will be cached for 1 hour upon creation.
 * If the client does not exist in the cache, it will be created using the options passed in
 * through the options.
 * @param {TableClientOptions} tableClientOptions The options to get the client for
 * @returns @type {QueueClient} Client
 */
const getClient = (queueClientOptions) => {
    const cacheItemName = `${queueClientOptions.accountName}-${queueClientOptions.queueName}`
    let cacheItem = clientCache.get(cacheItemName);
    if(!cacheItem) {
        const queueServiceClient = new QueueServiceClient(`https://${queueClientOptions.accountName}.queue.core.windows.net`, getCredential(queueClientOptions, 'queue'));
        const queueClient = queueServiceClient.getQueueClient(queueClientOptions.queueName);
        clientCache.set(cacheItemName, queueClient);
        return queueClient;
    }
    return cacheItem;
}

/**
 * Queue store for accessing azure storage queues.
 */
class QueueStore {
    /**
     * Creates a queueStore instance. This is used for accessing the table stores in an Azure storage Queue within a storage account.
     * 
     * The required parameters are one of the following
     *      tableName
     *      accountName
     * 
     * The optional combinations for authentication are the following:
     *      accountKey and accountName (for a shared key credential)
     *      clientSecret, clientId, tenantId (for a client secret credential)
     *      useIdentity set to true.
     * 
     * useIdentity will be used if available. If not, then we will check for the account key. Finally it will fall through to clientId, clientSecret and tenantId.
     * @param {object} queueClientOptions queue client options for client creation. Object structure: {accountKey: 'string', accountName: 'string', queueName: 'string', useIdentity: bool}
     */
    constructor(queueClientOptions) {
        if(!queueClientOptions) throw new AderantAzureStorageError('A queueClientOptions parameter is required when initializing the QueueStore class.');
        this.accountKey = queueClientOptions.accountKey;
        this.accountName = queueClientOptions.accountName;
        this.tenantId = queueClientOptions.tenantId;
        this.queueName = queueClientOptions.queueName;
        this.useIdentity = queueClientOptions.useIdentity;
        /**@type {QueueClient} */
        this.queueClient = getClient(queueClientOptions);
    }

    /**Get the name of the storage account this QueueStore belongs to. 
     * @returns {string} The name of the storage account.
     */
    getAccountName = () => {
        return this.queueClient.accountName;
    }

    /**Get the name of the queue this QueueStore is connected to. 
     * @returns {string} The name of the queue.
     */
    getQueueName = () => {
        return this.queueClient.name;
    }
    
    /**
     * Get messages given the messageCount from the given queue. Minimum = 1, maximum is 32. If you request less than 1 or greater than 32 you will get either the minimum
     * or maximum, whichever is closest to the number provided.
     * @param {Number} messageCount number of messages to get in a batch
     * @returns {DequeuedMessageItem[]} An array of dequeued messages.
     */
    getMessages = async (messageCount = 1, isBase64 = false) => {
        if(messageCount < 1) messageCount = 1;
        if(messageCount > 32) messageCount = 32;
        let messages = await this.queueClient.receiveMessages(messageCount);
        if(isBase64) {
            return JSON.parse(messages.map(x => Buffer.from(x,'base64').toString()));
        }
        return messages;
    }

    /**
     * Inserts a message into the queue used when creating the QueueStore.
     * @param {string} messageAsString JSON.stringified object or string for a message to be inserted into the queue.
     * @returns {QueueSendMessageResponse} The enqueued message result.
     */
    sendMessage = async(messageAsString, convertToBase64 = false) => {
        let message = messageAsString;
        if(convertToBase64) {
            message = Buffer.from(message).toString('base64')
        }
        return await this.queueClient.sendMessage(message);
    }

    /**
     * Sends the given array of messages to the given queue.
     * @param {string[]} messagesAsStringArray array of JSON.stringified objects or strings to be inserted into the queue.
     * @returns {QueueSendMessageResponse[]} the enqueued messages.
     */
    sendMessages = async(messagesAsStringArray, convertToBase64 = false) => {
        const queueResults = [];
        for(let i = 0; i < messagesAsStringArray.length;i++) {
            queueResults.push(await this.sendMessage(messagesAsStringArray[i], convertToBase64));
        }
        return queueResults;
    }

    /**
     * Peek messages within the given queue.
     * @returns {PeekedMessageItem[]} A list of the next messages in the queue.
     */
    peekMessages = async() => {
        return await this.queueClient.peekMessages();
    }

    /**
     * Deletes the given message from the queue.
     * @param {QueueMessage} messageToDelete The original queue message which was retreived from the queue to delete.
     * @returns {QueueDeleteMessageResponse} The delete message response
     */
    deleteMessage = async(messageToDelete) => {
        return await this.queueClient.deleteMessage(messageToDelete.messageId, messageToDelete.popReceipt)
    }

    // /**
    //  * Deletes the given queue messages from the queue.
    //  * @param {QueueMessage[]} messagesToDelete Array of queue messages to delete.
    //  * @returns {QueueDeleteMessageResponse[]} The response to the delete message requests.
    //  */
    // deleteMessages = async(messagesToDelete) => {
    //     const queueResults = [];
    //     for(let i = 0; i < messagesToDelete.length;i++) {
    //         queueResults.push(await this.deleteMessage(messagesToDelete[i]));
    //     }
    //     return queueResults;
    // }
}

module.exports = {QueueStore};